import { Box, Flex, Heading, HStack, Icon, Image, keyframes, Text, useToast, VStack } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { RefObject, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Link, NavigateFunction, useNavigate } from "react-router-dom";
import { FiShield, FiLink, FiGift, FiCheck, FiUsers } from "react-icons/fi";
import { CustomButton } from "@lubbezposrednio-frontend/commons";
import { Command, bindToCommand, ToastType, AuthorizationModel } from "@lubbezposrednio-frontend/core";
import { CyberspaceButton } from "../components/CyberspaceButton";
import { startCyberspaceOAuth } from "../utils/cyberspaceAuth";
import { CyberIdButton } from "../components/CyberIdButton";
import { startCyberidLogin } from "../utils/cyberidAuth";

interface AuthViewWrapperProps {
  authorization: AuthorizationModel;
  children: React.ReactNode;
  bottomElement?: React.ReactNode;
  buttonText: string;
  buttonColor?: string;
  customTop?: React.ReactNode;
  title?: string;
  subtitle?: string;
  cyberspaceOAuth?: boolean;
  cyberidLogin?: boolean;
  submitAction: (reCaptchaRef: RefObject<ReCAPTCHA>, toast: ToastType, navigate: NavigateFunction) => Command<void, any>;
}

// Logowanie przez Cyberspace tymczasowo ukryte (do czasu uruchomienia integracji). Włącz: true.
const CYBERSPACE_LOGIN_ENABLED = false;

const FEATURES = [
  { icon: FiShield, title: "Anonimowo i weryfikowalnie", desc: "Głosujesz anonimowo, a poprawność wyniku każdy może sprawdzić." },
  { icon: FiLink, title: "Zakotwiczone na blockchainie", desc: "Protokół jest niepodrabialny i odporny na manipulację." },
  { icon: FiGift, title: "Za darmo", desc: "Twórz głosowania bez opłat i ukrytych kosztów." }
];

const float = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(0, -26px) scale(1.06); }
`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Orb: React.FC<{ size: string; color: string; top?: string; bottom?: string; left?: string; right?: string; delay?: string; opacity?: number }> = ({ size, color, top, bottom, left, right, delay = "0s", opacity = 0.5 }) => (
  <Box position="absolute" top={top} bottom={bottom} left={left} right={right} w={size} h={size} bg={color} opacity={opacity} borderRadius="full" filter="blur(70px)" animation={`${float} 12s ease-in-out infinite`} sx={{ animationDelay: delay }} />
);

export const AuthViewWrapper: React.FC<AuthViewWrapperProps> = observer(
  ({ authorization, children, buttonText, bottomElement, buttonColor, submitAction, customTop, title, subtitle, cyberspaceOAuth, cyberidLogin }) => {
    const toast = useToast();
    const navigate = useNavigate();
    const reCaptchaRef = useRef<ReCAPTCHA>(null);
    const [cyberidLoading, setCyberidLoading] = useState(false);

    const handleCyberidLogin = async () => {
      setCyberidLoading(true);
      try {
        await startCyberidLogin();
        // sukces = redirect przeglądarki na CyberID; zostaw stan ładowania do opuszczenia strony
      } catch {
        setCyberidLoading(false);
        toast({ title: "Nie udało się rozpocząć logowania przez CyberID", status: "error", duration: 4000, isClosable: true });
      }
    };

    return (
      <Box position="relative" minH="100vh" bgGradient="linear(150deg, #25004f 0%, #430098 44%, #6a1fc2 100%)">
        {/* warstwa poświat (klipowana, nie psuje scrolla) */}
        <Box position="absolute" inset="0" overflow="hidden" pointerEvents="none">
          <Orb size="500px" color="#8b2fd6" top="-150px" left="-100px" opacity={0.38} />
          <Orb size="420px" color="#E51F44" bottom="-130px" right="-80px" opacity={0.18} delay="3s" />
          <Orb size="360px" color="#a45cff" top="42%" left="34%" opacity={0.22} delay="6s" />
          <Box position="absolute" inset="0" bgGradient="radial(circle at 22% 14%, rgba(255,255,255,0.10), transparent 42%)" />
        </Box>

        {/* reCAPTCHA poza szklaną kartą — backdrop-filter łapałby jej fixed-badge w karcie */}
        <ReCAPTCHA sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY as string} size="invisible" ref={reCaptchaRef} />

        <Flex position="relative" minH="100vh">
          {/* ── Brand (desktop) ── */}
          <Flex
            display={{ base: "none", lg: "flex" }}
            w="46%"
            maxW="640px"
            direction="column"
            justify="space-between"
            p="56px"
            color="white"
            animation={`${fadeUp} 0.6s ease`}
          >
            <Link to="/" aria-label="Strona główna">
              <Image src="/LBP_Logo_White.svg" alt="lubbezpośrednio.pl" h="36px" alignSelf="flex-start" />
            </Link>

            <Box>
              <Heading fontSize="40px" lineHeight="1.14" fontWeight="800" letterSpacing="-0.6px" mb="30px">
                Weryfikowalne głosowania,
                <br />
                którym można zaufać.
              </Heading>
              <VStack align="stretch" spacing="20px">
                {FEATURES.map(f => (
                  <HStack key={f.title} align="flex-start" spacing="15px">
                    <Flex w="44px" h="44px" borderRadius="14px" bg="rgba(255,255,255,0.13)" backdropFilter="blur(6px)" border="1px solid rgba(255,255,255,0.18)" align="center" justify="center" flexShrink={0}>
                      <Icon as={f.icon} boxSize="21px" />
                    </Flex>
                    <Box>
                      <Text fontWeight="700" fontSize="16px">
                        {f.title}
                      </Text>
                      <Text fontSize="14px" opacity={0.82} lineHeight="1.5">
                        {f.desc}
                      </Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            </Box>

            <Box bg="rgba(255,255,255,0.10)" backdropFilter="blur(14px)" border="1px solid rgba(255,255,255,0.18)" borderRadius="18px" p="16px 20px" maxW="400px" boxShadow="0 20px 50px rgba(0,0,0,0.22)">
              <HStack justify="space-between" mb="9px">
                <Text fontSize="11.5px" fontWeight="700" opacity={0.8} letterSpacing="0.04em" textTransform="uppercase">
                  Protokół głosowania
                </Text>
                <HStack spacing="5px" bg="rgba(47,206,37,0.22)" px="9px" py="3px" borderRadius="full">
                  <Icon as={FiCheck} boxSize="12px" />
                  <Text fontSize="11px" fontWeight="700">
                    Zakotwiczony
                  </Text>
                </HStack>
              </HStack>
              <Text fontFamily="mono" fontSize="13px" opacity={0.92} noOfLines={1}>
                0x9f2a·c41e·7b08·e3d6·a55f·1c90
              </Text>
              <Text fontSize="12px" opacity={0.7} mt="7px">
                2 940 głosów · niezmienny · publicznie weryfikowalny
              </Text>
            </Box>
          </Flex>

          {/* ── Forma w szklanej karcie ── */}
          <Flex flex="1" align="center" justify="center" px={{ base: "16px", md: "40px" }} py={{ base: "32px", md: "48px" }}>
            <Box
              w="100%"
              maxW="448px"
              bg="rgba(255,255,255,0.95)"
              backdropFilter="blur(24px)"
              border="1px solid rgba(255,255,255,0.6)"
              borderRadius="28px"
              boxShadow="0 30px 80px rgba(20,0,55,0.45)"
              p={{ base: "26px 20px", md: "34px 36px" }}
              animation={`${fadeUp} 0.5s ease`}
            >
              <Link to="/" aria-label="Strona główna">
                <Image src="/LBP_Logo_Black.svg" alt="lubbezpośrednio.pl" h="28px" mb="30px" display={{ base: "block", lg: "none" }} />
              </Link>

              {title && (
                <Heading fontSize={{ base: "26px", md: "29px" }} fontWeight="800" color="primary" letterSpacing="-0.5px" lineHeight="1.1" mb={subtitle ? "7px" : "20px"}>
                  {title}
                </Heading>
              )}
              {subtitle && (
                <Text color="gray.500" fontSize="14.5px" mb="22px" lineHeight="1.5">
                  {subtitle}
                </Text>
              )}

              {/* Baner: przyszedłeś z zaproszenia do grupy — po zalogowaniu/rejestracji dołączysz automatycznie. */}
              {(() => {
                const groupName = typeof window !== "undefined" ? localStorage.getItem("pendingGroupJoinName") : null;
                if (!groupName) return null;
                return (
                  <HStack align="flex-start" spacing="11px" bg="rgba(67,0,152,0.06)" border="1px solid rgba(67,0,152,0.12)" borderRadius="14px" p="12px 14px" mb="20px">
                    <Flex w="34px" h="34px" borderRadius="10px" bg="rgba(67,0,152,0.12)" align="center" justify="center" flexShrink={0}>
                      <Icon as={FiUsers} color="primary" boxSize="17px" />
                    </Flex>
                    <Box>
                      <Text fontSize="13.5px" fontWeight="700" color="primary">Dołączasz do grupy „{groupName}"</Text>
                      <Text fontSize="12px" color="gray.500">Po zalogowaniu automatycznie dodamy Cię do tej grupy.</Text>
                    </Box>
                  </HStack>
                );
              })()}

              {customTop}

              <VStack align="stretch" spacing="13px" w="100%">
                {children}
                <CustomButton text={buttonText} {...bindToCommand(submitAction(reCaptchaRef, toast, navigate))} backgroundColor={buttonColor} w="100%" py="15px" fontSize="16px" mt="2px" />
                {cyberspaceOAuth && CYBERSPACE_LOGIN_ENABLED && (
                  <>
                    <HStack spacing="12px" w="100%" py="2px">
                      <Box flex="1" h="1px" bg="rgba(67,0,152,0.12)" />
                      <Text fontSize="13px" color="gray.400" fontWeight="600">
                        lub
                      </Text>
                      <Box flex="1" h="1px" bg="rgba(67,0,152,0.12)" />
                    </HStack>
                    <CyberspaceButton onClick={() => startCyberspaceOAuth({ purpose: "login" })} />
                  </>
                )}
                {cyberidLogin && (
                  <>
                    <HStack spacing="12px" w="100%" py="2px">
                      <Box flex="1" h="1px" bg="rgba(67,0,152,0.12)" />
                      <Text fontSize="13px" color="gray.400" fontWeight="600">
                        lub
                      </Text>
                      <Box flex="1" h="1px" bg="rgba(67,0,152,0.12)" />
                    </HStack>
                    <CyberIdButton onClick={handleCyberidLogin} isLoading={cyberidLoading} />
                  </>
                )}
                {bottomElement}
              </VStack>
            </Box>
          </Flex>
        </Flex>
      </Box>
    );
  }
);
